import { useEffect, useMemo, useState } from 'react';
import { Box, SimpleGrid, Text, Loader, Center } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { getRelatedProducts } from '@/Api';
import { clientRoutes, websiteDomain } from '@/ApiPaths';
import { ProductCard } from '@/components/ProductCard';
import type { IProduct } from '@/types/Product.model';
import type { UserProfile } from '@/store/Store';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';

const RELATED_LIMIT = 4;

type RelatedProduct = Partial<Omit<IProduct, 'owningUser_id'>> & {
  _id: string;
  owningUser_id?: Partial<UserProfile> | string;
};

type RelatedProductsSectionProps = {
  productId: string;
  /** Owner company — recommendations stay within this brand. */
  companyName: string;
  tags: string[];
  category?: string | null;
  accentColor?: string;
};

/**
 * Bottom-of-page recommendations: same company, matched by tags / category (max 4).
 * Uses a capped related endpoint — does not pull the full marketplace list.
 */
export function RelatedProductsSection({
  productId,
  companyName,
  tags,
  category,
  accentColor = DEFAULT_QR_CODE_COLOR,
}: RelatedProductsSectionProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<RelatedProduct[]>([]);
  const [loading, setLoading] = useState(false);

  const trimmedCompany = companyName.trim();
  const canFetch = Boolean(trimmedCompany) && tags.length > 0;
  const tagsKey = useMemo(() => tags.join('\0'), [tags]);

  useEffect(() => {
    if (!canFetch || !productId) {
      setItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getRelatedProducts({
      excludeId: productId,
      company: trimmedCompany,
      tags,
      // Category scoring available server-side when UI re-enables it
      category: category ?? null,
      limit: RELATED_LIMIT,
    })
      .then((related) => {
        if (!cancelled) setItems(related as RelatedProduct[]);
      })
      .catch((error) => {
        console.error('Failed to load related products:', error);
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, trimmedCompany, tagsKey, category, canFetch]);

  if (!canFetch) return null;
  if (!loading && items.length === 0) return null;

  return (
    <Box
      style={{
        width: '100%',
        maxWidth: '1300px',
        marginTop: 28,
        marginBottom: 40,
        paddingInline: 4,
      }}
    >
      <Text
        fw={700}
        size="lg"
        mb="md"
        style={{
          borderLeft: `3px solid ${accentColor}`,
          paddingLeft: 12,
        }}
      >
        {t('productViewer.relatedProducts.title')}
      </Text>
      <Text size="sm" c="dimmed" mb="lg">
        {t('productViewer.relatedProducts.subtitle')}
      </Text>

      {loading ? (
        <Center py="xl">
          <Loader color={accentColor} size="sm" />
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md">
          {items.map((product) => (
            <ProductCard
              key={product._id}
              product={{
                ...product,
                owningUser_id:
                  typeof product.owningUser_id === 'string' ? undefined : product.owningUser_id,
              }}
              showArticleNumber={false}
              showListingStatus
              showPrice
              corporateColor={accentColor}
              onClick={() => {
                window.location.href = `${websiteDomain}${clientRoutes.product}`.replace(
                  ':id',
                  product._id
                );
              }}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}
